import { DayOfWeek, LocalDate } from "@js-joda/core";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  DayNumber,
  DaySlot,
  MonthHeader,
  MonthView,
  MonthRow,
  MonthRowSpacer,
  MonthRowSticky,
  MonthRowStickyCard,
  MonthHeaderCard,
} from "./scheduleMonthView.styled";
import { ScheduleWorkerRow } from "web-apps/company/components/schedule/types/schedule.types";
import {
  formatDateKey,
  formatItemDateKey,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import {
  ScheduleRow,
  ScheduleCreatePayload,
  ShiftClickPayload,
} from "../../common/scheduleRow/scheduleRow.component";
import { ScheduleViewBody } from "../shared/scheduleViewBody.component";
import { GroupByOption } from "web-apps/company/components/schedule/components/hooks/useScheduleToolbarFilters";
import { GroupDivider } from "../shared/groupDivider.component";
import { getRowRenderKey } from "../shared/scheduleRow.key.logic";

interface Props {
  currentDate: LocalDate;
  onChangeDate: (nextDate: LocalDate) => void;
  shiftBlockSize: "small" | "large" | "pill";
  isLoading: boolean;
  isLoadingMore: boolean;
  isEmpty: boolean;
  emptyText: string;
  groupByOption: GroupByOption;
  workerRows: ScheduleWorkerRow[];
  onShiftClick: (payload: ShiftClickPayload) => void;
  onScheduleClick: (payload: ScheduleCreatePayload) => void;
  loadMoreRef: RefObject<HTMLDivElement>;
}

interface MonthDatesRowProps {
  currentDate: LocalDate;
  monthDays: LocalDate[];
  dayCountStyle: React.CSSProperties;
  leftColumnWidth: number;
  monthGridGap: number;
}

const LEFT_COLUMN_WIDTH = 220;
const MONTH_GRID_GAP = 12;

const MonthDatesRow: React.FC<MonthDatesRowProps> = (props) => (
  <MonthRow
    style={props.dayCountStyle}
    $leftColumnWidth={props.leftColumnWidth}
    $gap={props.monthGridGap}
  >
    <MonthRowSpacer />
    {props.monthDays.map((date) => {
      const isWeekend =
        date.dayOfWeek() === DayOfWeek.SATURDAY ||
        date.dayOfWeek() === DayOfWeek.SUNDAY;
      const isSelected = date.equals(props.currentDate);
      return (
        <DaySlot key={date.toString()} $isSelected={isSelected}>
          <DayNumber $isWeekend={isWeekend} $isSelected={isSelected}>
            {date.dayOfMonth()}
          </DayNumber>
        </DaySlot>
      );
    })}
  </MonthRow>
);

export const ScheduleMonthView: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [showStickyDates, setShowStickyDates] = useState(false);
  const monthStart = useMemo(
    () => props.currentDate.withDayOfMonth(1),
    [props.currentDate],
  );
  const daysInMonth = monthStart.lengthOfMonth();

  const monthDays = useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, index) =>
        monthStart.plusDays(index),
      ),
    [daysInMonth, monthStart],
  );
  const dayCountStyle = useMemo(
    () =>
      ({
        "--day-count": String(daysInMonth),
      }) as React.CSSProperties,
    [daysInMonth],
  );
  const monthDayKeys = useMemo(
    () => new Set(monthDays.map((date) => formatDateKey(date))),
    [monthDays],
  );

  const visibleWorkerRows = useMemo(
    () =>
      props.workerRows.filter((workerRow) =>
        workerRow.sections.some((section) =>
          section.items.some((item) =>
            monthDayKeys.has(formatItemDateKey(item.startTime)),
          ),
        ),
      ),
    [monthDayKeys, props.workerRows],
  );
  const isViewEmpty = !props.isLoading && visibleWorkerRows.length === 0;

  const rowsWithDividers = useMemo<
    Array<{
      key: string;
      row: ScheduleWorkerRow;
      divider: JSX.Element | null;
    }>
  >(() => {
    if (props.groupByOption !== "team") {
      return visibleWorkerRows.map((workerRow) => ({
        key: getRowRenderKey(workerRow, props.groupByOption),
        row: workerRow,
        divider: null,
      }));
    }

    const result: Array<{
      key: string;
      row: ScheduleWorkerRow;
      divider: JSX.Element | null;
    }> = [];
    let lastWorkGroupId: number | null = null;
    visibleWorkerRows.forEach((workerRow) => {
      const section = workerRow.sections[0];
      const workGroupId = section?.id ?? null;
      const rawUnitName = (section?.unitName ?? "").trim();
      const unitName =
        rawUnitName.length > 0 ? rawUnitName : t("Schedule.UnknownUnit");
      const rawTeamName = (section?.text ?? "").trim();
      const teamName =
        rawTeamName.length > 0 ? rawTeamName : t("Schedule.UnknownTeam");
      const shouldRenderDivider =
        workGroupId !== null && workGroupId !== lastWorkGroupId;
      result.push({
        key: getRowRenderKey(workerRow, props.groupByOption),
        row: workerRow,
        divider: shouldRenderDivider ? (
          <GroupDivider
            key={`divider-${workGroupId}`}
            unitName={unitName}
            teamName={teamName}
          />
        ) : null,
      });
      lastWorkGroupId = workGroupId;
    });
    return result;
  }, [props.groupByOption, t, visibleWorkerRows]);

  useEffect(() => {
    const element = headerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyDates(!entry.isIntersecting),
      { rootMargin: "-16px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <MonthView>
      <MonthHeaderCard ref={headerRef}>
        <MonthHeader>
          <PeriodNavigator
            currentDate={props.currentDate}
            mode="month"
            onChange={props.onChangeDate}
            showSubLabel
          />
        </MonthHeader>
        <MonthDatesRow
          currentDate={props.currentDate}
          monthDays={monthDays}
          dayCountStyle={dayCountStyle}
          leftColumnWidth={LEFT_COLUMN_WIDTH}
          monthGridGap={MONTH_GRID_GAP}
        />
      </MonthHeaderCard>
      <MonthRowSticky $visible={showStickyDates}>
        <MonthRowStickyCard>
          <MonthDatesRow
            currentDate={props.currentDate}
            monthDays={monthDays}
            dayCountStyle={dayCountStyle}
            leftColumnWidth={LEFT_COLUMN_WIDTH}
            monthGridGap={MONTH_GRID_GAP}
          />
        </MonthRowStickyCard>
      </MonthRowSticky>
      <ScheduleViewBody
        isLoading={props.isLoading}
        isEmpty={props.isEmpty || isViewEmpty}
        emptyText={props.emptyText}
        isLoadingMore={props.isLoadingMore}
        loadMoreRef={props.loadMoreRef}
        hasContent={visibleWorkerRows.length > 0}
        isDimmed={props.isLoading && visibleWorkerRows.length > 0}
      >
        {rowsWithDividers.map((entry) => (
          <div key={entry.key}>
            {entry.divider}
            <ScheduleRow
              workerRow={entry.row}
              days={monthDays}
              shiftBlockSize={props.shiftBlockSize}
              gridGap={MONTH_GRID_GAP}
              variant="month"
              onShiftClick={props.onShiftClick}
              onScheduleClick={props.onScheduleClick}
            />
          </div>
        ))}
      </ScheduleViewBody>
    </MonthView>
  );
};
